// @ts-nocheck
/**
 * Site-wide recipe search (snippets/vhp-search.liquid), modelled on Instagram's search:
 *  - opening it shows recent searches (queries, ingredients, categories, recipes; each removable)
 *    and popular ingredients,
 *  - typing shows live rows: the query itself, ingredients ("Tofu · 75 Rezepte"), categories and recipes,
 *  - submitting shows a 3-column results grid with tabs (Rezepte / Zutaten / Kategorien) and filters,
 *  - an ingredient opens its own page (like a hashtag) where more ingredients can be combined:
 *    "Tofu + Brokkoli" = recipes with both; none with all of them -> the ones with the most of them.
 * All matching runs on Algolia via assets/vhp-search-core.js.
 *
 * Every step is a history entry (state.vhpSearch), so the phone's back button walks back through
 * results -> suggestions -> closed, and coming back from a recipe restores the search.
 * Opening a recipe plays the result list as a vertical video reel over the search (assets/vhp-reel-viewer.js,
 * "Rezept ansehen" there opens the recipe sheet). "Alle swipen", or when the reel cannot load, starts the swipe
 * deck with the list (sessionStorage `vhp_explore_set`, read by assets/vhp-home-feed.js); without the app shell
 * that is the recipe page.
 *
 * Filters: the app's one filter sheet (assets/vhp-filters.js) in "search" mode. A search starts from the standing
 * filters of the top bar and keeps its own changes (in its history entry). Protein, time and kcal run on Algolia;
 * meal, "Ohne", the extras and "Noch nicht geliked" (this device's likes) filter the rows client-side.
 *
 *   window.vhpSearch.open({ view: 'results', protein: 40 })
 *   window.vhpSearch.count(filters) -> Promise<number>   (the sheet's "N Rezepte anzeigen" in browse mode)
 */
(() => {
  'use strict';

  const tr = window.vhpT; // snippets/vhp-i18n.liquid

  const root = document.querySelector('[data-vhp-search]');
  const core = window.vhpSearchCore;
  const filtersApi = window.vhpFilters;
  if (!root || !core || !filtersApi || root.dataset.bound === '1') return;
  root.dataset.bound = '1';

  const SET_KEY = 'vhp_explore_set';
  const MAX_SET = 40;
  const SORT_LABEL = filtersApi.SORT_LABEL;
  const POPULAR = ['Tofu', 'Tempeh', 'Seitan', 'Linsen', 'Kichererbsen', 'Bohnen', 'Edamame', 'Haferflocken', 'Proteinpulver', 'Erdnussbutter', 'Skyr'];
  // Quick chips above the results; "Noch nicht geliked" leads once this device has likes.
  const QUICK = [
    ['sort', 'density', tr('filters.sort_density')],
    ['protein', '40', tr('search.quick_protein')],
    ['time', '15', tr('search.quick_time')],
    ['kcal', '0-500', tr('search.quick_kcal')],
    ['props', 'lowcarb', tr('filters.props_lowcarb')],
    ['kcal', '700-', tr('search.quick_bulk')],
  ];
  const FRESH = ['props', 'fresh', tr('filters.props_fresh')];

  const input = root.querySelector('[data-s-input]');
  const form = root.querySelector('[data-s-form]');
  const clearBtn = root.querySelector('[data-s-clear]');
  const backBtn = root.querySelector('[data-s-back]');
  const cancelBtn = root.querySelector('[data-s-cancel]');
  const body = root.querySelector('[data-s-body]');
  const shell = document.body.classList.contains('app-shell-on');
  const rootUrl = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ---------------------------------------------------------------- helpers */

  const esc = (value) =>
    String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const track = (name, params) => {
    if (typeof window.vhpTrack === 'function') window.vhpTrack(name, params);
  };
  const articleUrl = (handle) => `/blogs/rezepte/${encodeURIComponent(handle)}`;
  const sized = (url, width) => (url ? `${url}${url.includes('?') ? '&' : '?'}width=${width}` : '');
  const recipeWord = (n) => tr('common.recipes', { n });
  const metaLine = (row, withKcal) =>
    [
      row.protein ? tr('common.protein', { n: Math.round(row.protein) }) : '',
      row.time ? tr('common.min', { n: Math.round(row.time) }) : '',
      withKcal && row.kcal ? `${Math.round(row.kcal)} kcal` : '',
    ]
      .filter(Boolean)
      .join(' · ');

  const ICON = {
    search: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></svg>',
    leaf: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 19c0-8 5-13 14-14 0 9-5 14-13 14"/><path d="M5 19c3-4 6-7 10-9"/></svg>',
    grid: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="4" width="7" height="7" rx="2"/><rect x="13" y="4" width="7" height="7" rx="2"/><rect x="4" y="13" width="7" height="7" rx="2"/><rect x="13" y="13" width="7" height="7" rx="2"/></svg>',
    x: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>',
    plus: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>',
    swipe: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="6" y="3" width="12" height="18" rx="3"/><path d="M10.5 9.5 13 12l-2.5 2.5"/></svg>',
    filter: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M4 7h9M17 7h3M4 17h3M11 17h9"/><circle cx="15" cy="7" r="2"/><circle cx="9" cy="17" r="2"/></svg>',
    heart: '<svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" aria-hidden="true"><path d="M12 21s-7.5-4.6-9.6-9.3C1 8.4 3 5 6.4 5c2 0 3.6 1.1 4.6 2.6h2C14 6.1 15.6 5 17.6 5 21 5 23 8.4 21.6 11.7 19.5 16.4 12 21 12 21z"/></svg>',
  };

  const likedHandles = () => {
    try {
      return new Set((window.vhpPlannerLikes?.read().likes.items || []).map((item) => item.handle).filter(Boolean));
    } catch (_) {
      return new Set();
    }
  };

  // Marks the typed words in a title, when normalising kept the title's length (no "ß").
  const highlight = (title, q) => {
    const n = core.norm(title);
    if (n.length !== title.length) return esc(title);
    const words = core.norm(q).split(/[\s,+]+/).filter((w) => w.length > 1);
    const marks = new Array(title.length).fill(false);
    words.forEach((word) => {
      let at = n.indexOf(word);
      while (at !== -1) {
        for (let i = at; i < at + word.length; i += 1) marks[i] = true;
        at = n.indexOf(word, at + word.length);
      }
    });
    let out = '';
    for (let i = 0; i < title.length; i += 1) {
      if (marks[i] && !marks[i - 1]) out += '<mark>';
      out += esc(title[i]);
      if (marks[i] && !marks[i + 1]) out += '</mark>';
    }
    return out;
  };

  /* ---------------------------------------------------------------- state + history */

  const blank = () => ({ view: 'home', q: '', ings: [], ...filtersApi.blank(), sort: '', tab: 'recipes', add: false, base: null, depth: 0 });
  let st = null;
  let isOpen = false;
  let current = [];

  const filterCount = (s = st) => filtersApi.count(filtersApi.clean(s));
  // Diet and "Noch nicht geliked" run here, not on Algolia: the index has no tags, and the likes live on this
  // device (assets/vhp-planner-likes.js).
  const applyClient = (rows, s = st) => {
    const f = filtersApi.clean(s);
    if (!filtersApi.needsFacts(f) && !f.props.includes('fresh')) return rows;
    const liked = f.props.includes('fresh') ? likedHandles() : null;
    return rows.filter((row) => filtersApi.passes({ ...row, liked: Boolean(liked?.has(row.handle)) }, f));
  };
  const query = (s = st) => ({ q: s.q, ings: s.ings, protein: s.protein, time: s.time, kcal: s.kcal });

  const writeHistory = (method) => {
    try {
      history[method]({ ...(method === 'replaceState' ? history.state || {} : {}), vhpSearch: st }, '');
    } catch (_) {}
  };

  const show = () => {
    if (isOpen) return;
    isOpen = true;
    root.hidden = false;
    document.documentElement.classList.add('vhp-s-open');
  };

  const hide = () => {
    isOpen = false;
    st = null;
    root.hidden = true;
    document.documentElement.classList.remove('vhp-s-open');
    if (document.activeElement === input) input.blur();
    const sheet = document.getElementById('app-filters');
    try {
      if (sheet?.dataset.fMode === 'search') sheet.hidePopover();
    } catch (_) {}
  };

  const push = (next) => {
    st = { ...blank(), ...next, depth: (isOpen && st ? st.depth : 0) + 1 };
    writeHistory('pushState');
    show();
    render();
  };

  const replace = (patch) => {
    st = { ...st, ...patch };
    writeHistory('replaceState');
    render();
  };

  const close = () => {
    if (!isOpen) return;
    const depth = st?.depth || 0;
    if (depth > 0 && history.state?.vhpSearch) history.go(-depth);
    else hide();
  };

  const back = () => {
    if (history.state?.vhpSearch && st?.depth > 1) history.back();
    else close();
  };

  window.addEventListener('popstate', (event) => {
    const saved = event.state && event.state.vhpSearch;
    if (!saved) {
      if (isOpen) hide();
      return;
    }
    // Back from the reel or the recipe sheet on top: same search step, keep the grid and its scroll.
    if (isOpen && st && saved.depth === st.depth && saved.view === st.view) return;
    st = saved;
    show();
    render();
  });

  /* ---------------------------------------------------------------- rows */

  const queryRow = (q) =>
    `<button type="button" class="vhp-s-row" data-s-q="${esc(q)}"><span class="vhp-s-ava vhp-s-ava--icon">${ICON.search}</span><span class="vhp-s-row__txt"><span class="vhp-s-row__title">${esc(q)}</span></span></button>`;

  const ingRow = (ing, q) =>
    `<button type="button" class="vhp-s-row" data-s-ing="${esc(ing.key)}" data-label="${esc(ing.name)}"><span class="vhp-s-ava vhp-s-ava--ing">${ICON.leaf}</span><span class="vhp-s-row__txt"><span class="vhp-s-row__title">${q ? highlight(ing.name, q) : esc(ing.name)}</span><span class="vhp-s-row__sub">${tr('search.ingredient')}${ing.count ? ` · ${recipeWord(ing.count)}` : ''}</span></span>${st?.add ? `<span class="vhp-s-row__add">${ICON.plus}</span>` : ''}</button>`;

  const catUrl = (cat) => (shell ? `${rootUrl()}?feed=${encodeURIComponent(cat.handle)}&fl=${encodeURIComponent(cat.title)}` : `/collections/${encodeURIComponent(cat.handle)}`);

  const catRow = (cat, q) =>
    `<a class="vhp-s-row" href="${esc(catUrl(cat))}" data-s-cat="${esc(cat.handle)}" data-label="${esc(cat.title)}" data-img="${esc(cat.image || '')}"><span class="vhp-s-ava vhp-s-ava--cat">${cat.image ? `<img src="${esc(cat.image)}" alt="" loading="lazy" width="44" height="44">` : ICON.grid}</span><span class="vhp-s-row__txt"><span class="vhp-s-row__title">${q ? highlight(cat.title, q) : esc(cat.title)}</span><span class="vhp-s-row__sub">${tr('search.category')}${cat.count ? ` · ${recipeWord(cat.count)}` : ''}</span></span></a>`;

  const recipeRow = (row, q, from) =>
    `<a class="vhp-s-row" href="${esc(articleUrl(row.handle))}" data-s-open data-handle="${esc(row.handle)}" data-from="${from}"><span class="vhp-s-ava vhp-s-ava--sq">${row.image ? `<img src="${esc(sized(row.image, 120))}" alt="" loading="lazy" width="44" height="44">` : ''}</span><span class="vhp-s-row__txt"><span class="vhp-s-row__title">${q ? highlight(row.title, q) : esc(row.title)}</span><span class="vhp-s-row__sub">${esc(metaLine(row, true) || tr('common.recipe'))}</span></span></a>`;

  const recentRow = (e) => {
    let row;
    if (e.t === 'ing') row = ingRow({ key: e.k, name: e.label, count: core.ingredient(e.k).count }, '');
    else if (e.t === 'cat') row = catRow({ handle: e.k, title: e.label, image: e.img, count: 0 }, '');
    else if (e.t === 'rec') {
      const rec = core.data()?.byHandle.get(e.h || e.k) || { handle: e.h || e.k, title: e.label, image: e.img };
      row = recipeRow(rec, '', 'recent');
    } else row = queryRow(e.label);
    return `<li class="vhp-s-recent">${row}<button type="button" class="vhp-s-recent__x" data-s-remove="${esc(e.t)}|${esc(e.k)}" aria-label="${esc(tr('common.remove', { label: e.label }))}">${ICON.x}</button></li>`;
  };

  /* ---------------------------------------------------------------- home: recent + popular */

  const popularChips = () =>
    core
      .popular(POPULAR)
      .map((ing) => `<button type="button" class="vhp-s-pchip" data-s-ing="${esc(ing.key)}" data-label="${esc(ing.name)}">${esc(ing.name)}</button>`)
      .join('');

  const paintHome = () => {
    if (st.add) {
      const have = (st.base?.ings || []).map((k) => core.ingredient(k).name);
      body.innerHTML = `<p class="vhp-s-hint">${esc(tr('search.add_hint', { have: have.join(' + ') || tr('search.all') }))}</p>
        <p class="vhp-s-label">${tr('search.popular_label')}</p><div class="vhp-s-pchips">${popularChips()}</div>`;
      return;
    }
    const recent = core.recents.list();
    body.innerHTML =
      (recent.length
        ? `<div class="vhp-s-head"><p class="vhp-s-label">${tr('search.recent')}</p><button type="button" class="vhp-s-link" data-s-recent-clear>${tr('search.clear_all')}</button></div>
           <ul class="vhp-s-list" role="list">${recent.map(recentRow).join('')}</ul>`
        : '') +
      `<p class="vhp-s-label">${tr('search.popular_label')}</p><div class="vhp-s-pchips">${popularChips()}</div>` +
      (recent.some((e) => e.t === 'ing')
        ? ''
        : `<p class="vhp-s-hint">${esc(tr('search.tip'))}</p>`);
  };

  /* ---------------------------------------------------------------- typing: suggestions */

  let suggestToken = 0;
  const paintSuggest = () => {
    const q = st.q.trim();
    const token = ++suggestToken;
    const top = st.add ? '' : queryRow(q);
    const box = body.querySelector('[data-s-suggest]');
    if (!box) body.innerHTML = `<div class="vhp-s-list" data-s-suggest role="listbox" aria-label="${tr('search.suggestions')}">${top}</div>`;
    else if (top) {
      const first = box.querySelector('[data-s-q]');
      if (first) first.outerHTML = top;
    }
    core.suggest(q).then(
      (res) => {
        if (token !== suggestToken || !isOpen || st.view !== 'home') return;
        const box = body.querySelector('[data-s-suggest]');
        if (!box) return;
        if (st.add) {
          const ings = core.matchIngredients(q, 8).filter((ing) => !(st.base?.ings || []).includes(ing.key));
          box.innerHTML = ings.length ? ings.map((ing) => ingRow(ing, q)).join('') : `<p class="vhp-s-hint">${esc(tr('search.no_ingredient', { q }))}</p>`;
          return;
        }
        const ings = res.ingredients.map((ing) => ingRow(ing, q));
        const cats = res.categories.map((cat) => catRow(cat, q));
        const recs = res.recipes.map((row) => recipeRow(row, q, 'suggest'));
        // One word that is an ingredient: the ingredient first ("tofu" -> Tofu, then recipes).
        const ingFirst = !/\s/.test(q) && res.ingredients[0] && core.norm(res.ingredients[0].name).startsWith(core.norm(q));
        const rows = ingFirst ? ings.concat(cats, recs) : cats.concat(recs, ings);
        box.innerHTML = top + rows.join('');
      },
      () => {}
    );
  };

  /* ---------------------------------------------------------------- results */

  // Cards fade in one after another (--i, capped so a long list does not wait); the photo fades in on load. The one
  // recipe card look (snippets/vhp-recipe-card.liquid card_tile): title and facts (.vhp-facts, assets/app-shell.css).
  const tileHtml = (row, liked, have, total, i) => `<a class="vhp-s-tile${row.image ? '' : ' is-loaded'}${liked.has(row.handle) ? ' is-liked' : ''}" href="${esc(articleUrl(row.handle))}" data-s-open data-handle="${esc(row.handle)}" role="listitem" style="--i:${Math.min(i, 11)}">
      ${row.image ? `<img class="vhp-s-tile__img" src="${esc(sized(row.image, 480))}" alt="" loading="${i < 6 ? 'eager' : 'lazy'}" decoding="async" width="480" height="600">` : ''}
      <span class="vhp-s-tile__img vhp-s-tile__img--ph" aria-hidden="true">${ICON.leaf}</span>
      <span class="vhp-s-tile__scrim" aria-hidden="true"></span>
      ${liked.has(row.handle) ? `<span class="vhp-s-tile__liked" aria-label="${tr('search.liked')}">${ICON.heart}</span>` : ''}
      ${have ? `<span class="vhp-s-tile__have">${tr('search.have', { have, total })}</span>` : row.exclusive ? `<span class="vhp-s-tile__have vhp-s-tile__have--exclusive">${tr('common.exclusive')}</span>` : ''}
      <span class="vhp-s-tile__body"><span class="vhp-s-tile__title">${esc(row.title)}</span><span class="vhp-facts">${row.protein ? `<span class="vhp-fact vhp-fact--protein"><strong>${Math.round(row.protein)}g</strong> Protein</span>` : ''}${row.time ? `<span class="vhp-fact vhp-fact--time"><strong>${Math.round(row.time)}</strong> ${esc(tr('common.min', { n: '' }).trim())}</span>` : ''}</span></span>
    </a>`;

  const skeletonHtml = (n) =>
    '<span class="vhp-s-tile vhp-s-tile--ph" aria-hidden="true"><span class="vhp-s-sk vhp-s-sk--title"></span><span class="vhp-s-sk vhp-s-sk--chip"></span></span>'.repeat(n);

  // Photos already in the cache never fire "load": mark them right away.
  const markLoaded = (grid) =>
    grid.querySelectorAll('.vhp-s-tile__img:not(.vhp-s-tile__img--ph)').forEach((img) => {
      if (img.complete && img.naturalWidth) img.closest('.vhp-s-tile').classList.add('is-loaded');
    });

  const resultsLabel = () => {
    if (st.q.trim()) return tr('search.quoted', { q: st.q.trim() });
    if (st.ings.length) return st.ings.map((k) => core.ingredient(k).name).join(' + ');
    if (st.sort && !filterCount()) return SORT_LABEL[st.sort];
    return tr('search.your_results');
  };

  const filterRow = () => {
    const n = filterCount();
    const quickList = st.props.includes('fresh') || likedHandles().size ? [FRESH, ...QUICK] : QUICK;
    const isQuick = (key, value) => quickList.some(([k, v]) => k === key && v === String(value));
    const quick = quickList.map(([key, value, label]) => {
      const on = key === 'sort' ? st.sort === value : filtersApi.isOn(st, key, value);
      return `<button type="button" class="vhp-s-qchip" data-s-quick="${key}:${value}" aria-pressed="${on}">${esc(label)}</button>`;
    });
    // Sheet values that no quick chip shows (e.g. 30 g+ Protein, Ohne Soja) get a removable pill.
    const extra = filtersApi
      .active(st)
      .filter((a) => !isQuick(a.key, a.value))
      .map((a) => [`${a.key}:${a.value}`, a.label]);
    if (st.sort && !isQuick('sort', st.sort)) extra.push([`sort:${st.sort}`, SORT_LABEL[st.sort]]);
    return `<div class="vhp-s-filters" role="group" aria-label="${tr('common.filters')}">
      <button type="button" class="vhp-s-qchip vhp-s-qchip--icon" data-s-filters aria-label="${n ? tr('search.filters_active', { n }) : tr('common.filters')}">${ICON.filter}${n ? `<span class="vhp-s-badge">${n}</span>` : ''}</button>
      ${extra.map(([key, label]) => `<button type="button" class="vhp-s-qchip" aria-pressed="true" data-s-unset="${esc(key)}" aria-label="${esc(tr('common.remove', { label }))}">${esc(label)} ${ICON.x}</button>`).join('')}
      ${quick.join('')}
    </div>`;
  };

  const ingChips = () =>
    st.ings
      .map((k) => `<button type="button" class="vhp-s-ichip" data-s-ing-remove="${esc(k)}" aria-label="${esc(tr('common.remove', { label: core.ingredient(k).name }))}">${esc(core.ingredient(k).name)} ${ICON.x}</button>`)
      .join('') + `<button type="button" class="vhp-s-ichip vhp-s-ichip--add" data-s-ing-add>${ICON.plus} ${tr('search.ingredient')}</button>`;

  const heroHtml = () => {
    const names = st.ings.map((k) => core.ingredient(k).name);
    const single = st.ings.length === 1;
    return `<header class="vhp-s-hero">
      <span class="vhp-s-hero__ava">${ICON.leaf}</span>
      <div class="vhp-s-hero__txt">
        <h2 class="vhp-s-hero__title">${esc(names.join(' + '))}</h2>
        <p class="vhp-s-hero__sub" data-s-count>…</p>
      </div>
    </header>
    <div class="vhp-s-hero__actions">
      ${single ? `<button type="button" class="vhp-s-btn" data-s-ing-add>${ICON.plus} ${tr('search.combine')}</button>` : ''}
      <button type="button" class="vhp-s-btn vhp-s-btn--primary" data-s-swipe hidden>${ICON.swipe} ${tr('search.swipe_all')}</button>
    </div>
    ${single ? '' : `<div class="vhp-s-ichips">${ingChips()}</div>`}`;
  };

  const tabsHtml = (ings, cats) => {
    if (!ings.length && !cats.length) return '';
    const tab = (id, label) => `<button type="button" class="vhp-s-tab" role="tab" data-s-tab="${id}" aria-selected="${st.tab === id}">${label}</button>`;
    return `<div class="vhp-s-tabs" role="tablist">${tab('recipes', tr('search.tab_recipes'))}${ings.length ? tab('ings', tr('search.tab_ingredients')) : ''}${cats.length ? tab('cats', tr('search.tab_categories')) : ''}</div>`;
  };

  // No results: each filter's removal with the count it would bring back.
  const paintRelax = (box) => {
    const options = filtersApi
      .active(st)
      .filter((a) => a.relax)
      .map((a) => [a.relaxPatch || a.unset, a.relax]);
    if (st.ings.length > 1) st.ings.forEach((k) => options.push([{ ings: st.ings.filter((x) => x !== k) }, tr('search.without', { label: core.ingredient(k).name })]));
    if (filterCount() > 1 && (st.q.trim() || st.ings.length)) options.push([filtersApi.blank(), tr('search.no_filters', { label: resultsLabel() })]);
    const token = resultsToken;
    options.forEach(([patch, label]) => {
      core.search(query({ ...st, ...patch })).then((res) => {
        const n = applyClient(res.rows, { ...st, ...patch }).length;
        if (token !== resultsToken || !n) return;
        if (!box.childElementCount) box.insertAdjacentHTML('beforeend', `<p class="vhp-s-relaxes__label">${tr('search.relax_label')}</p>`);
        box.insertAdjacentHTML('beforeend', `<button type="button" class="vhp-s-relax" data-s-relax='${esc(JSON.stringify(patch))}'>${esc(label)} <span>${n}</span></button>`);
      }, () => {});
    });
  };

  let resultsToken = 0;
  const paintResults = () => {
    const token = ++resultsToken;
    const q = st.q.trim();
    const hero = st.ings.length && !q;
    const ingMatches = q && !st.ings.length ? core.matchIngredients(q, 20) : [];
    const catMatches = q && !st.ings.length ? core.matchCategories(q, 20) : [];
    if (st.tab !== 'recipes' && !(st.tab === 'ings' ? ingMatches : catMatches).length) st.tab = 'recipes';

    let head = hero ? heroHtml() : tabsHtml(ingMatches, catMatches);
    if (!hero && st.ings.length) head += `<div class="vhp-s-ichips">${ingChips()}</div>`;

    if (st.tab === 'ings') {
      body.innerHTML = `${head}<div class="vhp-s-list">${ingMatches.map((ing) => ingRow(ing, q)).join('')}</div>`;
      return;
    }
    if (st.tab === 'cats') {
      body.innerHTML = `${head}<div class="vhp-s-list">${catMatches.map((cat) => catRow(cat, q)).join('')}</div>`;
      return;
    }

    // A filter change on the same results: the old cards stay (dimmed) until the new ones arrive, no flash.
    const prevGrid = body.querySelector('[data-s-grid]:not([aria-busy])');
    const prevTiles = prevGrid && prevGrid.querySelector('[data-handle]') ? prevGrid.innerHTML : '';
    const prevCount = body.querySelector('[data-s-count]')?.textContent || '';
    const chipScroll = body.querySelector('.vhp-s-filters')?.scrollLeft || 0;

    body.innerHTML = `${head}<div class="vhp-s-sticky">${filterRow()}
      ${hero ? '' : `<div class="vhp-s-meta"><p class="vhp-s-count" data-s-count aria-live="polite">${esc(prevCount || tr('search.searching'))}</p><button type="button" class="vhp-s-btn vhp-s-btn--primary vhp-s-btn--sm" data-s-swipe hidden>${ICON.swipe} ${tr('search.swipe_all')}</button></div>`}</div>
      <p class="vhp-s-note" data-s-note hidden></p>
      <div class="vhp-s-grid${prevTiles ? ' is-stale' : ''}" data-s-grid role="list" aria-busy="true">${prevTiles || skeletonHtml(6)}</div>
      <div class="vhp-s-empty" data-s-empty hidden></div>`;
    const filters = body.querySelector('.vhp-s-filters');
    if (filters) filters.scrollLeft = chipScroll;

    core.search(query()).then(
      (res) => {
        if (token !== resultsToken || !isOpen || st.view !== 'results') return;
        const rows = applyClient(res.mode === 'some' && !st.sort ? res.rows : core.sortRows(res.rows, st.sort));
        current = rows;
        const liked = likedHandles();
        const grid = body.querySelector('[data-s-grid]');
        grid.removeAttribute('aria-busy');
        grid.classList.remove('is-stale');
        grid.innerHTML = rows.map((row, i) => tileHtml(row, liked, res.have ? res.have.get(row.id) : 0, st.ings.length, i)).join('');
        markLoaded(grid);
        const count = body.querySelector('[data-s-count]');
        if (count) count.textContent = recipeWord(rows.length);
        body.querySelectorAll('[data-s-swipe]').forEach((btn) => (btn.hidden = rows.length < 2));
        const note = body.querySelector('[data-s-note]');
        const notes = {
          similar: tr('search.similar', { q }),
          some: tr('search.some'),
        };
        note.textContent = notes[res.mode] || '';
        note.hidden = !notes[res.mode];
        const empty = body.querySelector('[data-s-empty]');
        empty.hidden = rows.length > 0;
        if (!rows.length) {
          empty.innerHTML = `<span class="vhp-s-empty__ico" aria-hidden="true">${ICON.search}</span><p class="vhp-s-empty__title">${tr('search.none_title')}</p><p>${tr('search.none_text')}</p><div class="vhp-s-relaxes" data-s-relaxes></div>
            <p class="vhp-s-label">${tr('search.try_ingredient')}</p><div class="vhp-s-pchips">${popularChips()}</div>`;
          paintRelax(empty.querySelector('[data-s-relaxes]'));
        }
        if (st.tracked !== JSON.stringify(query())) {
          st.tracked = JSON.stringify(query());
          writeHistory('replaceState');
          if (q) track('explore_search', { q, results: rows.length, source: 'submit' });
          if (st.ings.length) track('search_ingredients', { ings: st.ings.join('+'), results: rows.length });
        }
      },
      () => {
        if (token !== resultsToken) return;
        const grid = body.querySelector('[data-s-grid]');
        grid.removeAttribute('aria-busy');
        grid.classList.remove('is-stale');
        grid.innerHTML = '';
        const empty = body.querySelector('[data-s-empty]');
        empty.hidden = false;
        empty.innerHTML = `<p>${tr('search.offline')}</p>${q ? `<a class="vhp-s-link" href="/search?q=${encodeURIComponent(q)}">${tr('search.classic')}</a>` : ''}`;
      }
    );
  };

  /* ---------------------------------------------------------------- render */

  const paintBar = () => {
    const results = st.view === 'results';
    backBtn.hidden = !(results || st.add);
    cancelBtn.hidden = results || st.add;
    root.classList.toggle('is-results', results);
    // Back from the suggestions to the results: the keyboard goes away with them.
    if (results && document.activeElement === input) input.blur();
    input.placeholder = st.add ? tr('search.add_placeholder') : tr('search.placeholder');
    if (document.activeElement !== input) input.value = results && st.ings.length && !st.q ? '' : st.q;
    clearBtn.hidden = !input.value;
  };

  const render = () => {
    if (!st) return;
    paintBar();
    if (st.view === 'results') paintResults();
    else if (st.q.trim()) paintSuggest();
    else paintHome();
    if (st.view === 'results') body.scrollTop = 0;
  };

  /* ---------------------------------------------------------------- filter sheet */

  // Recipes a search state would show (the sheet's "N Rezepte anzeigen").
  const countFor = (s) => core.search(query(s)).then((res) => applyClient(res.rows, s).length);

  // The app's filter sheet (assets/vhp-filters.js) on this search's own filters.
  const openSheet = () => {
    if (!st) return;
    filtersApi.open({
      mode: 'search',
      get: () => ({ ...filtersApi.clean(st), sort: st.sort }),
      set: ({ sort, ...patch }) => {
        const before = st.sort;
        Object.assign(st, filtersApi.clean({ ...st, ...patch }));
        if (sort !== undefined) st.sort = filtersApi.cleanSort(sort);
        if (st.sort && st.sort !== before) track('explore_sort', { sort: st.sort, source: 'search' });
        replace({});
      },
      count: () => countFor(st),
    });
  };

  /* ---------------------------------------------------------------- actions */

  const openInFeed = (handles, start, label) => {
    const list = handles.slice(0, MAX_SET);
    if (!list.includes(start)) list.unshift(start);
    track('explore_result_click', { start, size: list.length, source: 'search' });
    if (!shell) {
      window.location.href = articleUrl(start);
      return;
    }
    try {
      sessionStorage.setItem(SET_KEY, JSON.stringify({ handles: list, start, label, at: Date.now() }));
    } catch (_) {
      window.location.href = articleUrl(start);
      return;
    }
    window.location.href = `${rootUrl()}?set=explore`;
  };

  // The reel (assets/vhp-reel-viewer.js) loads on the first recipe tap; the recipe sheet is on every page.
  let reelLoading = null;
  const loadReel = () => {
    if (window.vhpReelViewer && window.vhpRecipeSheet) return Promise.resolve(window.vhpReelViewer);
    if (!reelLoading) {
      const urls = (root.dataset.reelAssets || '').split(' ').filter(Boolean);
      const add = (url) =>
        new Promise((resolve, reject) => {
          const css = /\.css(\?|$)/.test(url);
          if (document.querySelector(css ? `link[href="${url}"]` : `script[src="${url}"]`)) return resolve();
          const el = css ? Object.assign(document.createElement('link'), { rel: 'stylesheet', href: url }) : Object.assign(document.createElement('script'), { src: url, async: false });
          el.onload = resolve;
          el.onerror = reject;
          document.head.appendChild(el);
        });
      reelLoading = Promise.all(urls.map(add)).then(
        () => window.vhpReelViewer || null,
        () => {
          reelLoading = null;
          return null;
        }
      );
    }
    return reelLoading;
  };

  // Tap on a result: the results as a vertical video reel, starting at that recipe. Without it: the swipe deck.
  const openInReel = (rows, handle, fallback) => {
    const start = Math.max(0, rows.findIndex((row) => row.handle === handle));
    loadReel().then((viewer) => {
      if (!viewer || !isOpen) {
        fallback();
        return;
      }
      track('explore_result_click', { start: handle, size: rows.length, source: 'search_reel' });
      viewer.open({
        items: rows.map((row) => ({ id: row.pid || '', handle: row.handle, title: row.title, image: sized(row.image, 720), protein: row.protein, time: row.time, kcal: row.kcal })),
        start,
        label: `${resultsLabel()} als Videos`,
        source: 'search',
        // Subscribers plan a recipe straight from the reel (the day/meal sheet, assets/vhp-plan-picker.js, as on Likes).
        ...(root.dataset.subscriber === 'true'
          ? window.vhpPlanPicker
            ? { onPlan: (item) => window.vhpPlanPicker.open(item, { source: 'search_reel' }) }
            : { planHref: (item) => `${root.dataset.plannerUrl || '/pages/meal-planner'}#einplanen=${encodeURIComponent(item.id)}` }
          : {}),
        onClose: (last) => body.querySelector(`.vhp-s-tile[data-handle="${CSS.escape(last.handle)}"]`)?.scrollIntoView({ block: 'nearest' }),
        originFor: (item) => body.querySelector(`.vhp-s-tile[data-handle="${CSS.escape(item.handle)}"]`),
      });
    });
  };

  // Load the reel while the finger is still down, and the tapped recipe's video with it.
  root.addEventListener(
    'pointerdown',
    (event) => {
      const tile = event.target.closest?.('[data-s-open]');
      if (!tile) return;
      loadReel().then((viewer) => viewer?.prefetch(tile.dataset.handle));
    },
    { passive: true }
  );

  const submit = (text) => {
    const q = String(text == null ? input.value : text).trim();
    if (st.add) {
      const first = core.matchIngredients(q, 1)[0];
      if (first) addIngredient(first.key, first.name);
      return;
    }
    if (!q) return;
    input.blur();
    core.recents.add({ t: 'q', k: core.norm(q), label: q });
    push({ ...st, view: 'results', q, ings: [], tab: 'recipes', add: false, base: null, tracked: '' });
  };

  const addIngredient = (key, label) => {
    core.recents.add({ t: 'ing', k: key, label });
    if (st.add && st.base) {
      const ings = st.base.ings.includes(key) ? st.base.ings : st.base.ings.concat(key);
      input.value = '';
      input.blur();
      replace({ ...st.base, view: 'results', ings, add: false, base: null, tab: 'recipes', tracked: '' });
      return;
    }
    input.blur();
    push({ ...st, view: 'results', q: '', ings: [key], tab: 'recipes', add: false, base: null, tracked: '' });
  };

  // One filter value (URL parameters): protein | time | kcal | sort.
  const setFilter = (key, value) => {
    if (key === 'sort') st.sort = filtersApi.cleanSort(value);
    else Object.assign(st, filtersApi.clean({ ...st, [key]: value }));
  };

  // A chip (quick chip, pill): that value on or off. Sort is the search's own; the rest is the filter model.
  const toggleFilter = (key, value) => {
    if (key === 'sort') st.sort = st.sort === value ? '' : filtersApi.cleanSort(value);
    else Object.assign(st, filtersApi.clean({ ...st, ...filtersApi.toggle(st, key, value) }));
  };

  root.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const plain = !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0;

    const open = target.closest('[data-s-open]');
    if (open && plain) {
      event.preventDefault();
      const handle = open.dataset.handle;
      if (open.dataset.from === 'suggest' || open.dataset.from === 'recent') {
        const rec = core.data()?.byHandle.get(handle);
        core.recents.add({ t: 'rec', k: handle, h: handle, label: rec?.title || open.textContent.trim(), img: rec?.image ? sized(rec.image, 120) : '' });
        const list = open.dataset.from === 'suggest' ? Array.from(body.querySelectorAll('[data-s-open]')).map((a) => a.dataset.handle) : [handle];
        const rows = list.map((h) => core.data()?.byHandle.get(h)).filter(Boolean);
        const feed = () => openInFeed(list, handle, st.q.trim() ? tr('search.quoted', { q: st.q.trim() }) : tr('search.from_search'));
        if (rows.some((row) => row.handle === handle)) openInReel(rows, handle, feed);
        else feed();
      } else {
        openInReel(current, handle, () => openInFeed(current.map((row) => row.handle), handle, resultsLabel()));
      }
      return;
    }

    const cat = target.closest('[data-s-cat]');
    if (cat) {
      core.recents.add({ t: 'cat', k: cat.dataset.sCat, label: cat.dataset.label, img: cat.dataset.img || '' });
      track('explore_category_click', { handle: cat.dataset.sCat, source: 'search' });
      return; // the link navigates
    }

    const remove = target.closest('[data-s-remove]');
    if (remove) {
      const [t, ...rest] = remove.dataset.sRemove.split('|');
      core.recents.remove(t, rest.join('|'));
      paintHome();
      return;
    }

    if (target.closest('[data-s-recent-clear]')) {
      core.recents.clear();
      paintHome();
      return;
    }

    const q = target.closest('[data-s-q]');
    if (q) {
      input.value = q.dataset.sQ;
      submit(q.dataset.sQ);
      return;
    }

    const ing = target.closest('[data-s-ing]');
    if (ing) {
      addIngredient(ing.dataset.sIng, ing.dataset.label || core.ingredient(ing.dataset.sIng).name);
      return;
    }

    const ingRemove = target.closest('[data-s-ing-remove]');
    if (ingRemove) {
      const ings = st.ings.filter((k) => k !== ingRemove.dataset.sIngRemove);
      if (!ings.length && !st.q.trim()) back();
      else replace({ ings, tracked: '' });
      return;
    }

    if (target.closest('[data-s-ing-add]')) {
      const { depth, ...base } = st;
      push({ ...st, view: 'home', q: '', add: true, base });
      input.value = '';
      input.focus();
      return;
    }

    const tab = target.closest('[data-s-tab]');
    if (tab) {
      replace({ tab: tab.dataset.sTab });
      return;
    }

    const quick = target.closest('[data-s-quick]');
    if (quick) {
      const [key, value] = quick.dataset.sQuick.split(/:(.*)/);
      const on = quick.getAttribute('aria-pressed') === 'true';
      toggleFilter(key, value);
      replace({});
      if (!on) track('explore_quick_filter', { filter: quick.dataset.sQuick, source: 'search' });
      return;
    }

    const unset = target.closest('[data-s-unset]');
    if (unset) {
      const [key, value] = unset.dataset.sUnset.split(/:(.*)/);
      toggleFilter(key, key === 'protein' || key === 'time' ? Number(value) : value);
      replace({});
      return;
    }

    if (target.closest('[data-s-filters]')) {
      openSheet();
      return;
    }

    const relax = target.closest('[data-s-relax]');
    if (relax) {
      try {
        replace({ ...JSON.parse(relax.dataset.sRelax), tracked: '' });
      } catch (_) {}
      return;
    }

    if (target.closest('[data-s-swipe]')) {
      if (current.length) openInFeed(current.map((row) => row.handle), current[0].handle, resultsLabel());
    }
  });

  // Photo loaded: fade it in. Broken photo: the leaf placeholder stays.
  const onImg = (event) => {
    const img = event.target;
    if (!(img instanceof HTMLImageElement) || !img.classList.contains('vhp-s-tile__img')) return;
    img.closest('.vhp-s-tile')?.classList.add('is-loaded');
    if (event.type === 'error') img.remove();
  };
  body.addEventListener('load', onImg, true);
  body.addEventListener('error', onImg, true);

  backBtn.addEventListener('click', back);
  cancelBtn.addEventListener('click', close);

  clearBtn.addEventListener('click', () => {
    input.value = '';
    clearBtn.hidden = true;
    if (st.view === 'results') {
      push({ ...st, view: 'home', q: '', add: false });
    } else {
      st.q = '';
      writeHistory('replaceState');
      render();
    }
    input.focus();
  });

  // Tapping the field on the results shows the suggestions again (back returns to the results).
  input.addEventListener('focus', () => {
    if (st && st.view === 'results') push({ ...st, view: 'home', add: false });
  });

  let typing = 0;
  input.addEventListener('input', () => {
    if (!st) return;
    if (st.view === 'results') push({ ...st, view: 'home', add: false });
    st.q = input.value;
    clearBtn.hidden = !input.value;
    window.clearTimeout(typing);
    typing = window.setTimeout(() => {
      writeHistory('replaceState');
      if (st.q.trim()) paintSuggest();
      else paintHome();
    }, 90);
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    submit();
  });

  // Instagram hides the keyboard as soon as the list scrolls.
  body.addEventListener('scroll', () => {
    if (document.activeElement === input && body.scrollTop > 8) input.blur();
  }, { passive: true });

  const rowsIn = () => Array.from(body.querySelectorAll('.vhp-s-row'));
  const moveFocus = (direction) => {
    const items = rowsIn();
    if (!items.length) return;
    const next = items.indexOf(document.activeElement) + direction;
    if (next < 0) input.focus();
    else items[Math.min(next, items.length - 1)].focus();
  };

  root.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      if (st?.view === 'results' || st?.add) back();
      else close();
      return;
    }
    if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && (event.target === input || event.target.closest?.('.vhp-s-row'))) {
      if (event.target === input && event.key === 'ArrowUp') return;
      event.preventDefault();
      moveFocus(event.key === 'ArrowDown' ? 1 : -1);
    }
  });

  /* ---------------------------------------------------------------- opening */

  // A new search starts from the top bar's standing filters; `init` (an Explore chip ...) goes on top.
  const open = (init = {}) => {
    const view = init.view === 'results' ? 'results' : 'home';
    if (isOpen && st?.view === 'home' && view === 'home') {
      input.focus();
      return;
    }
    const seeded = { ...filtersApi.get(), ...init };
    push({ ...blank(), ...init, ...filtersApi.clean(seeded), sort: filtersApi.cleanSort(init.sort), view, tracked: '' });
    if (view === 'home') input.focus();
    core.load().then(() => {
      if (isOpen && st.view === 'home' && !st.q.trim()) paintHome();
    }, () => {});
  };

  // Any search trigger: [data-vhp-search-open] and the theme's old "#search-modal" buttons.
  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest?.('[data-vhp-search-open], [on\\:click="#search-modal/showDialog"]');
      if (!trigger) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      try {
        trigger.closest('[popover]')?.hidePopover();
      } catch (_) {}
      open();
    },
    { capture: true }
  );

  // A second tap on the Explore tab (on Explore): first back to the top, then into the search.
  document.addEventListener('click', (event) => {
    const tab = event.target.closest?.('.app-tab[data-tab="explore"]');
    if (!tab || !document.querySelector('[data-explore]') || event.metaKey || event.ctrlKey) return;
    event.preventDefault();
    if (isOpen) close();
    else if (window.scrollY > 40) window.scrollTo({ top: 0, behavior: 'smooth' });
    else open();
  });

  window.vhpSearch = {
    open,
    close,
    showFilters: openSheet,
    count: (values = {}) => countFor({ ...blank(), ...filtersApi.clean(values) }),
  };

  // Back from a recipe while searching: the history entry holds the search. Only on back / forward: a reload or a fresh
  // visit of the same URL opened on an old query (docs/PERSONA_ATTACK_PLAN.md 2.11).
  const navType = performance.getEntriesByType?.('navigation')?.[0]?.type || '';
  const saved = navType === 'back_forward' && history.state && history.state.vhpSearch;
  if (!saved && history.state?.vhpSearch) {
    const { vhpSearch, ...rest } = history.state;
    history.replaceState(Object.keys(rest).length ? rest : null, '');
  }
  if (saved) {
    st = saved;
    show();
    render();
  } else {
    // Shared links and the no-JS fallback (/search?q=, Explore ?q=&p=&t=&k=&s=).
    const params = new URLSearchParams(location.search);
    const onSearchPage = Boolean(document.querySelector('[data-explore]')) || /^search/.test(document.querySelector('main[data-template]')?.dataset.template || '');
    if (onSearchPage && ['q', 'p', 't', 'k', 's'].some((key) => params.get(key))) {
      const init = { view: 'results', q: (params.get('q') || '').trim() };
      push({ ...blank(), ...init });
      setFilter('protein', params.get('p'));
      setFilter('time', params.get('t'));
      setFilter('kcal', params.get('k'));
      setFilter('sort', params.get('s'));
      replace({});
    }
  }

  window.addEventListener('vhp:likes-changed', () => {
    if (isOpen && st?.view === 'results') {
      const liked = likedHandles();
      body.querySelectorAll('.vhp-s-tile[data-handle]').forEach((tile) => tile.classList.toggle('is-liked', liked.has(tile.dataset.handle)));
    }
  });
})();
